// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngPicture from "@pace/App/Components/Common/XngPicture";
import XngCarousel, { SwiperStyle } from "@pace/App/Components/XngCarousel";
import { customXngageWidget } from "@pace/XDL/constants";
import { isTouchDevice } from "@pace/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";
import { Helmet } from "react-helmet";

const enum fields {
    slides = "slides",
    autoScroll = "autoScroll",
    delay = "delay",
    animation = "animation",
    animationType = "animationType",
    showBullets = "showBullets",
    showArrows = "showArrows",
    showNumbers = "showNumbers",
    reducedHeight = "reducedHeight",
}

const enum slideFields {
    slideTitle = "slideTitle",
    slideSubText = "slideSubText",
    textColor = "textColor",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    buttonSize = "buttonSize",
    openInNewTab = "openInNewTab",
    smallPath = "smallPath",
    mediumPath = "mediumPath",
    largePath = "largePath",
    altText = "altText",
    background = "background",
    alignment = "alignment",
}

interface SlideModel {
    fields: {
        slideTitle: string;
        slideSubText: string;
        textColor: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        openInNewTab: boolean;
        buttonVariant: "primary" | "secondary" | "tertiary";
        buttonSize: "small" | "medium" | "large";
        smallPath: string;
        mediumPath: string;
        largePath: string;
        altText: string;
        background: string;
        alignment: "LeftToRight" | "RightToLeft";
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.slides]: SlideModel[];
        [fields.autoScroll]: boolean;
        [fields.delay]: number;
        [fields.animation]: number;
        [fields.animationType]: string;
        [fields.showBullets]: boolean;
        [fields.showArrows]: boolean;
        [fields.showNumbers]: boolean;
        [fields.reducedHeight]: boolean;
    };
    extendedStyles?: BannerCarouselStyles;
}

type Props = OwnProps;

export interface BannerCarouselStyles {
    widgetWrapper?: InjectableCss;
    slidesWrapper?: InjectableCss;
    carouselWrapper?: SwiperStyle;
    slideImageAndContentWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideTitle?: TypographyProps;
    slideSubTitle?: TypographyProps;
    slideButton?: ButtonPresentationProps;
    slideImageContainer?: InjectableCss;
    image?: LazyImageProps;
    point?: InjectableCss;
    innerPoint?: InjectableCss;
    sliderTextContainer?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    tippyPinWrapper?: InjectableCss;
    arrowStyle?: ClickablePresentationProps;
    notify?: TypographyProps;
    itemWrapper?: GridItemProps;
}

export const BannerCarousalStyles: BannerCarouselStyles = {};

const styles = BannerCarousalStyles;

const BannerCarousel: React.FC<Props> = ({ fields }) => {
    const {
        slides = [],
        autoScroll,
        delay = 10000,
        animation = 500,
        showArrows,
        showBullets,
        showNumbers,
        animationType,
    } = fields;
    const buttonLinks = useGetLinks(slides, o => o.fields.buttonLink || {});
    const { isInShell } = useShellContext();
    const history = useHistory();

    const openLink = (url?: string, inNewTab?: boolean) => {
        if (!url || isInShell) {
            return;
        }
        if (inNewTab) {
            window.open(url, "_blank", "noreferrer");
        } else if (url.startsWith("http")) {
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return (
        <StyledWrapper {...styles.widgetWrapper} data-test-selector="BannerCarousal-Wrapper">
            <StyledWrapper {...styles.slidesWrapper}>
                <XngCarousel
                    extendedStyles={styles.carouselWrapper}
                    navigation={showArrows && slides.length > 1}
                    pagination={
                        (showBullets || showNumbers) &&
                        slides.length > 1 && {
                            clickable: true,
                            el: ".swiper-pagination",
                            renderBullet: (index, className) =>
                                `<span class="${className} ${showNumbers ? "dots-with-numbers" : ""}">
                                    ${showNumbers ? index + 1 : ""}
                                </span>`,
                        }
                    }
                    autoplay={
                        autoScroll
                            ? {
                                  delay,
                                  disableOnInteraction: true,
                              }
                            : undefined
                    }
                    speed={animation}
                    effect={animationType.toLowerCase()}
                    draggable={isTouchDevice()}
                >
                    {slides.map((slide, index) => {
                        const buttonLink = buttonLinks[index];
                        return (
                            <GridItem {...styles.itemWrapper} key={`slide_${slide.fields.slideTitle}_${index}`}>
                                <StyledWrapper
                                    {...styles.slideImageAndContentWrapper}
                                    {...{
                                        isReversed: slide.fields.alignment === "RightToLeft",
                                        backgroundColor: slide.fields.background,
                                    }}
                                >
                                    <StyledWrapper
                                        {...styles.slideContentWrapper}
                                        {...{ color: slide.fields.textColor }}
                                    >
                                        {slide.fields.slideTitle && (
                                            <Typography
                                                {...styles.slideTitle}
                                                data-test-selector="BannerCarousel_Slide-title"
                                            >
                                                {parse(slide.fields.slideTitle, parserOptions)}
                                            </Typography>
                                        )}
                                        {slide.fields.slideSubText && (
                                            <Typography
                                                {...styles.slideSubTitle}
                                                data-test-selector="BannerCarousel_Slide-copy-content"
                                            >
                                                {parse(slide.fields.slideSubText, parserOptions)}
                                            </Typography>
                                        )}
                                        {/* Button URL must be there if we have a label. */}
                                        {!!slide.fields.buttonLabel?.trim() && !!buttonLink.url?.length && (
                                            <Button
                                                className="slide-button"
                                                {...styles.slideButton}
                                                variant={slide.fields.buttonVariant}
                                                sizeVariant={slide.fields.buttonSize}
                                                onClick={() => openLink(buttonLink.url, slide.fields.openInNewTab)}
                                                data-test-selector="BannerCarousel_Slide-action-button"
                                            >
                                                {slide.fields.buttonLabel}
                                            </Button>
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper
                                        data-test-selector="BannerCarousel_Slide-image-wrapper"
                                        {...styles.slideImageContainer}
                                        {...{ fullHeight: !fields.reducedHeight }}
                                    >
                                        {/** XNG-Changes: PAC-1119 */}
                                        <Helmet>
                                            <link
                                                rel="preload"
                                                href={slide.fields.largePath}
                                                as="image"
                                                {...{ fetchpriority: "high" }}
                                            />
                                        </Helmet>
                                        <XngPicture
                                            smallPath={slide.fields.smallPath}
                                            mediumPath={slide.fields.mediumPath}
                                            largePath={slide.fields.largePath}
                                            altText={slide.fields.altText || `Slide image ${slide.fields.slideTitle}`}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                        );
                    })}
                </XngCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const slidesTab = {
    displayName: "Slides",
    sortOrder: 0,
};

const definition: WidgetDefinition = {
    group: customXngageWidget as any,
    fieldDefinitions: [
        {
            name: fields.slides,
            editorTemplate: "ListField",
            tab: slidesTab,
            getDisplay: (item: HasFields) => {
                return item.fields.slideTitle;
            },
            defaultValue: [],
            fieldType: "Contextual",
            fieldDefinitions: [
                {
                    name: slideFields.slideTitle,
                    displayName: "Slide Title",
                    editorTemplate: "TextField",
                    isRequired: true,
                    defaultValue: "",
                },
                {
                    name: slideFields.slideSubText,
                    displayName: "Slide Sub Text",
                    editorTemplate: "RichTextField",
                    defaultValue: "",
                },
                {
                    name: slideFields.textColor,
                    displayName: "Text Color",
                    defaultValue: "#000",
                    editorTemplate: "ColorPickerField",
                },
                {
                    name: slideFields.buttonLabel,
                    displayName: "Button Label",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: slideFields.buttonLink,
                    displayName: "Button Link",
                    editorTemplate: "LinkField",
                    isVisible: (item: any) => !!item.fields[slideFields.buttonLabel],
                    defaultValue: { type: "Page", value: "" },
                },
                {
                    name: slideFields.buttonVariant,
                    displayName: "Button Variant",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Primary", value: "primary" },
                        { displayName: "Secondary", value: "secondary" },
                        { displayName: "Tertiary", value: "tertiary" },
                    ],
                    hideEmptyOption: true,
                    defaultValue: "primary",
                },
                {
                    name: slideFields.buttonSize,
                    displayName: "Button Size",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Small", value: "small" },
                        { displayName: "Medium", value: "medium" },
                        { displayName: "Large", value: "large" },
                    ],
                    hideEmptyOption: true,
                    defaultValue: "medium",
                },
                {
                    name: slideFields.openInNewTab,
                    displayName: "Open in new tab",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
                {
                    name: slideFields.smallPath,
                    displayName: "Small Image Path",
                    editorTemplate: "ImagePickerField",
                    isRequired: true,
                    defaultValue: "",
                },
                {
                    name: slideFields.mediumPath,
                    displayName: "Medium Image Path",
                    editorTemplate: "ImagePickerField",
                    isRequired: true,
                    defaultValue: "",
                },
                {
                    name: slideFields.largePath,
                    displayName: "Large Image Path",
                    editorTemplate: "ImagePickerField",
                    isRequired: true,
                    defaultValue: "",
                },
                {
                    name: slideFields.altText,
                    displayName: "Alternative text",
                    editorTemplate: "TextField",
                    isRequired: true,
                    defaultValue: "",
                },
                {
                    name: slideFields.background,
                    displayName: "Background",
                    defaultValue: "#E7EBEF",
                    editorTemplate: "ColorPickerField",
                },
                {
                    name: slideFields.alignment,
                    displayName: "Alignment",
                    editorTemplate: "DropDownField",
                    options: [
                        {
                            displayName: "Left to right",
                            value: "LeftToRight",
                        },
                        {
                            displayName: "Right to left",
                            value: "RightToLeft",
                        },
                    ],
                    defaultValue: "LeftToRight",
                },
            ],
        },
        {
            name: fields.autoScroll,
            displayName: "Autoplay",
            editorTemplate: "CheckboxField",
            fieldType: "General",
            tab: slidesTab,
            defaultValue: true,
        },
        {
            name: fields.delay,
            displayName: "Timer Speed",
            editorTemplate: "IntegerField",
            defaultValue: 10000,
            fieldType: "General",
            isVisible: item => item.fields.autoScroll,
            isRequired: true,
            tooltip: "Time delay for auto play in Milliseconds",
            tab: slidesTab,
        },
        {
            name: fields.animation,
            displayName: "Animation Speed",
            editorTemplate: "IntegerField",
            defaultValue: 500,
            isRequired: true,
            fieldType: "General",
            tooltip: "Time of animation",
            tab: slidesTab,
        },
        {
            name: fields.animationType,
            editorTemplate: "DropDownField",
            options: [
                {
                    displayName: "Fade",
                    value: "fade",
                },
                {
                    displayName: "Slide",
                    value: "slide",
                },
            ],
            isRequired: true,
            fieldType: "Contextual",
            tab: slidesTab,
            defaultValue: "slide",
        },
        {
            name: fields.showArrows,
            displayName: "Show Arrows",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            tab: slidesTab,
        },
        {
            name: fields.showBullets,
            displayName: "Show Bullets",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            tab: slidesTab,
        },
        {
            name: fields.showNumbers,
            displayName: "Show Number",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: slidesTab,
        },
        {
            name: fields.reducedHeight,
            displayName: "Reduced Height",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: slidesTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: BannerCarousel,
    definition,
};

export default widgetModule;
